import { versionTotals } from '@app/shared/price-calc';
import { formatPln } from '../catalog/money';
import type { Invoice } from './types';

const amount = (grosze: number | undefined) => (grosze === undefined ? '—' : formatPln(grosze));

type TotalsInvoice = Pick<Invoice, 'vatBp' | 'netGrosze' | 'vatGrosze' | 'grossGrosze'> & {
  state?: Invoice['state'];
  kind?: Invoice['kind'];
  lines?: ReadonlyArray<{ netGrosze?: number }>;
  advances?: Invoice['advances'];
};

/**
 * Net, VAT and gross of the invoice; an amount the role may not see is a dash (nothing at all without any). F40: a
 * final invoice (ROZ) shows the whole order and each advance it settles above "Do zapłaty"; an advance invoice (ZAL)
 * the advance received instead of an amount to pay.
 */
export function InvoiceTotals({ invoice }: { invoice: TotalsInvoice }) {
  if (invoice.netGrosze === undefined && invoice.vatGrosze === undefined && invoice.grossGrosze === undefined) return null;
  const lines = invoice.lines ?? [];
  const order = invoice.kind === 'ROZ' && lines.length > 0 && lines.every((l) => l.netGrosze !== undefined) ? versionTotals(lines.map((l) => l.netGrosze!), invoice.vatBp) : null;
  return (
    <dl className="inv-totals">
      {order && (
        <div>
          <dt>Wartość zamówienia (brutto)</dt>
          <dd>{formatPln(order.grossGrosze)}</dd>
        </div>
      )}
      {invoice.kind === 'ROZ' &&
        (invoice.advances ?? []).map((a) => (
          <div key={a.id}>
            <dt>Zaliczka {a.number}</dt>
            <dd>{a.grossGrosze === undefined ? '—' : formatPln(-a.grossGrosze)}</dd>
          </div>
        ))}
      <div>
        <dt>Netto</dt>
        <dd data-testid="invoice-net">{amount(invoice.netGrosze)}</dd>
      </div>
      <div>
        {/* an invoice from another program may have several rates: only the total VAT */}
        <dt>{invoice.state === 'zewnetrzna' ? 'VAT' : `VAT ${invoice.vatBp / 100}%`}</dt>
        <dd>{amount(invoice.vatGrosze)}</dd>
      </div>
      <div className="inv-gross">
        <dt>{invoice.kind === 'ZAL' ? 'Zapłacono' : 'Do zapłaty'}</dt>
        <dd data-testid="invoice-gross">{amount(invoice.grossGrosze)}</dd>
      </div>
    </dl>
  );
}
